import React, { useCallback, useEffect } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import { ActionButton, ActionButtonVariants } from 'components/action-button';
import { List, ListItem } from 'components/list';
import { useRemoveDeliveryAddress } from 'hooks/use-remove-delivery-address';
import { toast } from 'utils/toast';

import { useDeliveryAddressModalContext } from '../delivery-address-modal.context';
import { ActionStatus } from '../types';

import { StyledAddressDescription, StyledAddressTitle, StyledModalHeader } from './styled';

export const DeleteDeliveryAddress = ({ close }: { close: VoidFunction }) => {
  const { formatMessage } = useIntl();
  const { deliveryAddress, dispatchDeliveryAddressState, setDeliveryAddress } =
    useDeliveryAddressModalContext();

  const { removeDeliveryAddress, data: removeResult, loading, error } = useRemoveDeliveryAddress();

  const { addressLine1, location, city, state, zip, phoneNumber } = deliveryAddress?.address || {};

  const onClickYes = useCallback(() => {
    removeDeliveryAddress({
      variables: {
        input: {
          alias: location,
          addressLine1,
          city,
          phoneNumber,
        },
      },
    });
  }, [addressLine1, city, location, phoneNumber, removeDeliveryAddress]);

  useEffect(() => {
    if (!loading) {
      if (removeResult) {
        toast.success(formatMessage({ id: 'deliveryAddressDeletedSuccessfully' }));
      }
      if (error) {
        toast.error(formatMessage({ id: 'errorToDeleteDeliveryAddress' }));
      }
      if (error || removeResult) {
        close();
        setDeliveryAddress(undefined);
      }
    }
  }, [close, error, formatMessage, loading, removeResult, setDeliveryAddress]);

  const handleNoButton = useCallback(() => {
    setDeliveryAddress(undefined);
    dispatchDeliveryAddressState({
      type: ActionStatus.DELIVERY_ADDRESS,
    });
  }, [dispatchDeliveryAddressState, setDeliveryAddress]);

  const addressTitle = location ? location : addressLine1;
  const addressDescription = location ? addressLine1 : `${city}, ${state}`;
  const addressSubDescription = location ? `${city}, ${state} - ${zip}` : `${zip}`;

  return (
    <>
      <StyledModalHeader aria-label={formatMessage({ id: 'confirmDeleteDeliveryAddress' })}>
        {formatMessage({ id: 'confirmDeleteDeliveryAddress' })}
      </StyledModalHeader>
      <Box margin="2rem" flexDirection="column">
        <List>
          <ListItem>
            <Box padding="1rem 3.5rem" flexDirection="column">
              <StyledAddressTitle data-testid="delete-address-title">
                {addressTitle}
              </StyledAddressTitle>
              <StyledAddressDescription data-testid="delete-address-description">
                {addressDescription}
              </StyledAddressDescription>
              <StyledAddressDescription data-testid="delete-address-sub-description">
                {addressSubDescription}
              </StyledAddressDescription>
            </Box>
          </ListItem>
        </List>
        <Box margin="1rem 0 2rem 0">
          <ActionButton
            fullWidth
            onClick={onClickYes}
            isLoading={loading}
            disabled={loading}
            data-testid="yes-delete-address"
            aria-label={formatMessage({ id: 'yes' })}
            variant={ActionButtonVariants.PRIMARY}
          >
            {formatMessage({ id: 'yes' })}
          </ActionButton>
        </Box>
        <ActionButton
          fullWidth
          onClick={handleNoButton}
          disabled={loading}
          aria-label={formatMessage({ id: 'no' })}
          data-testid="no-delete-address"
          variant={ActionButtonVariants.OUTLINE}
        >
          {formatMessage({ id: 'no' })}
        </ActionButton>
      </Box>
    </>
  );
};
